import { defineStore } from 'pinia'
import { API, HOST } from '@/utils/http'
import { useChatStore, type ChatMessage } from '@/stores/chat'
import { useOrgStore } from '@/stores/org'

export interface PanelConversation {
  key: string
  kind: 'member' | 'agent' | null
  peer: any
  lastMessage: ChatMessage | null
  unreadCount: number
  updatedAt: number
  messages: ChatMessage[]
}

// Messaging (org panel) UI state. Conversation/message data lives in the
// `chat` store; this store keeps the panel state (active conversation,
// search, new-chat picker, agent catalog) and adapts `chat` conversations to
// the peer-centric shape the panel components consume.
export const useMessagingStore = defineStore('messaging', {
  state: () => ({
    activeKey: null as string | null, // conversation uuid open in the panel
    agents: [] as any[],
    agentsLoaded: false,
    // The search input lives in MessagingPanel but the results render in
    // ChatList, so the query lives here rather than in either component.
    searchQuery: '',
    newChatOpen: false,
  }),

  getters: {
    // DM conversations in the panel's shape: key + kind ('member'|'agent') + peer.
    conversations: (): PanelConversation[] => {
      const chat = useChatStore()
      return chat.conversations
        .filter((c) => c.kind === 'dm' && c.peer)
        .map((c) => ({
          key: c.uuid,
          kind: c.peerKind as PanelConversation['kind'],
          peer: c.peer,
          lastMessage: c.lastMessage,
          unreadCount: c.unreadCount,
          updatedAt: c.lastAt,
          messages: chat.flatMessages(c.uuid),
        }))
    },
    conversation() {
      return (key: string): PanelConversation | null => this.conversations.find((c) => c.key === key) || null
    },
    messages: () => (key: string) => useChatStore().flatMessages(key),
    activeConversation(): PanelConversation | null {
      return this.activeKey ? this.conversation(this.activeKey) : null
    },
    pendingAgent: () => (key: string) => useChatStore().pendingAgent(key),
  },

  actions: {
    setActive(key: string | null) {
      this.activeKey = key
    },
    setSearchQuery(query: string) {
      this.searchQuery = query
    },
    setNewChatOpen(open: boolean) {
      this.newChatOpen = open
      this.searchQuery = ''
    },
    // Every agent the org can start a DM with; the per-project roster is a
    // separate, narrower call.
    async fetchAgents() {
      if (this.agentsLoaded) return
      try {
        const orgId = useOrgStore().selectedOrg
        const response = await API({
          method: 'GET',
          url: `${HOST}/org/${orgId}/project-agents/catalog`,
        })
        this.agents = response.data
      } catch (e) {
        this.agents = []
      }
      this.agentsLoaded = true
    },
    async openConversation({ kind, peer }: { kind: string, peer: any }) {
      const chat = useChatStore()
      const uuid = await chat.openDm(kind === 'agent' ? { peerAgentId: peer.agent_id } : { peerMemberId: peer.member_id })
      this.activeKey = uuid
      chat.markRead(uuid)
      return uuid
    },
    async sendMessage({ key, text, attachments }: { key: string, text?: string, attachments?: any[] }) {
      return useChatStore().sendMessage({ uuid: key, text, attachments })
    },
    // Agent replies are triggered server-side; this only shows the typing
    // indicator until the reply lands over WS.
    notePendingAgent({ key }: { key: string }) {
      const conversation = this.conversation(key)
      if (!conversation || conversation.kind !== 'agent') return
      useChatStore().notePendingAgent({ uuid: key, name: conversation.peer.display_name })
    },
  },
})
